<template>
  <div id="lo-rename-request" class="premium-purchase-experience">
    <!-- Background Moving Gradients & Shapes -->
    <div class="animated-mesh-bg"></div>
    <div class="background-decorations">
      <div class="blur-blob blob-1"></div>
      <div class="blur-blob blob-2"></div>
      <div class="blur-blob blob-3"></div>
    </div>

    <v-container class="py-6 px-4 max-width-container position-relative">
      <!-- HERO BANNER (Page Header) -->
      <div class="hero-banner slide-up-animation">
        <!-- Floating Particles inside Header -->
        <div
          class="particle"
          v-for="n in 6"
          :key="'p' + n"
          :style="getParticleStyle(n)"
        ></div>

        <v-row class="align-center position-relative ma-0">
          <v-col
            cols="12"
            md="8"
            class="hero-text-col d-flex align-center gap-4"
          >
            <v-btn icon dark class="mr-2 back-btn-blur" @click="backPage">
              <v-icon size="24">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="header-icon-box">
              <v-icon size="32" dark>mdi-pencil-circle-outline</v-icon>
            </div>
            <div class="header-title-bar">
              <div class="badge-hero mb-2">Official LO Rename request</div>
              <h1 class="hero-title">
                LO Rename
                <span class="text-gradient-sky">Request Portal</span>
              </h1>
              <p class="hero-subtitle mb-0">
                Submit and process local organization name amendments. Ensure
                your proposed names follow official JCI India guidelines before
                proceeding.
              </p>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Loading State -->
      <v-row
        v-slot:placeholder
        v-if="loading"
        justify="center"
        align="center"
        class="py-12 position-relative"
      >
        <v-col cols="12" class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <p
            class="mt-4 text-subtitle-1 grey--text text--darken-1 font-weight-medium"
          >
            Fetching details from ERP database...
          </p>
        </v-col>
      </v-row>

      <!-- Main Columns -->
      <v-row v-else class="page-content position-relative">
        <!-- Row 1: Current LO details & Current President profile -->
        <v-col cols="12" md="6" class="d-flex flex-column">
          <!-- Section 1: Current LO Information -->
          <v-card
            class="member-profile-card slide-up-animation-1 flex-grow-1"
            elevation="2"
          >
            <div class="profile-header-badge" :style="badgeStyle">
              <span class="badge-status-dot" :style="dotStyle"></span
              >{{ LomStatusTxt }} LO
            </div>
            <div class="card-section-title mb-6">
              <v-icon class="mr-2" color="primary"
                >mdi-office-building-marker</v-icon
              >
              <span>Current LO details</span>
            </div>

            <v-row dense>
              <v-col
                cols="12"
                sm="6"
                v-for="(item, index) in currentLoFields"
                :key="index"
                class="pa-2"
              >
                <div class="member-field-item">
                  <div class="mr-3 icon-wrap-bg">
                    <v-icon size="20" color="primary">{{ item.icon }}</v-icon>
                  </div>
                  <div>
                    <span class="field-lbl">{{ item.label }}</span>
                    <span class="field-val">{{ item.value || "N/A" }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex flex-column">
          <!-- Section 2: Current President Details -->
          <v-card
            class="member-profile-card slide-up-animation-2 flex-grow-1"
            elevation="2"
          >
            <div class="card-section-title mb-6">
              <v-icon class="mr-2" color="primary"
                >mdi-account-circle-outline</v-icon
              >
              <span>Current President profile</span>
            </div>

            <div class="d-flex flex-column flex-sm-row align-center gap-6">
              <div class="avatar-glow">
                <div class="profile-avatar-initials">
                  <img
                    :src="PresidentPhoto || 'assets/img/avatars/default.png'"
                    class="profile-avatar-img"
                    alt="President"
                  />
                </div>
              </div>
              <div class="flex-grow-1 w-100">
                <div class="member-title-name mb-1">
                  {{ PresidentName || "N/A" }}
                </div>
                <div class="member-subtitle-id mb-4">
                  <span class="id-pill">ID: {{ PresidentId || "N/A" }}</span>
                </div>

                <v-row dense>
                  <v-col cols="12" sm="6" class="pa-2">
                    <div class="member-field-item">
                      <div>
                        <span class="field-lbl">Mobile Number</span>
                        <span class="field-val text-body-2 font-weight-bold">{{
                          PresidentMobile || "N/A"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" class="pa-2">
                    <div class="member-field-item">
                      <div>
                        <span class="field-lbl">Email Address</span>
                        <span class="field-val text-body-2 font-weight-bold">{{
                          PresidentEmail || "N/A"
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Row 2: New LO Name details & LO Name Preview -->
        <v-col cols="12" md="6" class="d-flex flex-column">
          <!-- Section 3: Rename Request Form -->
          <v-card
            class="member-profile-card slide-up-animation-3 flex-grow-1"
            elevation="2"
          >
            <div class="card-section-title mb-6">
              <v-icon class="mr-2" color="primary"
                >mdi-square-edit-outline</v-icon
              >
              <span>New LO Name details</span>
            </div>

            <v-form ref="form" v-model="valid">
              <v-row>
                <!-- State Selection -->
                <v-col cols="12" sm="6" class="py-2">
                  <span class="field-label-custom"
                    ><span class="red--text">*</span> State</span
                  >
                  <v-autocomplete
                    v-model="NewStateCode"
                    :items="StateCodeOptions"
                    item-text="text"
                    item-value="value"
                    :loading="StateCodeOptionsLoading"
                    outlined
                    dense
                    required
                    :rules="[(v) => !!v || 'State is required']"
                    placeholder="Select State"
                    class="rounded-lg font-outfit"
                  ></v-autocomplete>
                </v-col>

                <!-- District Selection -->
                <v-col cols="12" sm="6" class="py-2">
                  <span class="field-label-custom"
                    ><span class="red--text">*</span> District</span
                  >
                  <v-autocomplete
                    v-model="NewDistrictCode"
                    :items="DistrictCodeOptions"
                    item-text="text"
                    item-value="value"
                    :loading="DistrictCodeOptionsLoading"
                    outlined
                    dense
                    required
                    :rules="[(v) => !!v || 'District is required']"
                    placeholder="Select District"
                    class="rounded-lg font-outfit"
                  ></v-autocomplete>
                </v-col>

                <!-- City Selection -->
                <v-col cols="12" sm="6" class="py-2">
                  <span class="field-label-custom">
                    <span class="red--text">*</span> City
                    <small class="grey--text text--darken-1"
                      >(Note - City will prefix the application)</small
                    >
                  </span>
                  <v-autocomplete
                    v-model="NewCityCode"
                    :items="CityCodeOptions"
                    item-text="text"
                    item-value="value"
                    :loading="CityCodeOptionsLoading"
                    outlined
                    dense
                    required
                    :rules="[(v) => !!v || 'City is required']"
                    placeholder="Select City"
                    class="rounded-lg font-outfit"
                  ></v-autocomplete>
                </v-col>

                <!-- Proposed Name Extension -->
                <v-col cols="12" sm="6" class="py-2">
                  <span class="field-label-custom">
                    <span class="red--text">*</span> Title Name Extension
                    <small class="grey--text text--darken-1"
                      >(Please don't enter JCI &amp; City prefix)</small
                    >
                  </span>
                  <v-text-field
                    v-model="NewLoName"
                    outlined
                    dense
                    required
                    :rules="[(v) => !!v || 'Title Name Extension is required']"
                    placeholder="Enter extension name (e.g. PRIDE)"
                    class="rounded-lg font-outfit"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex flex-column">
          <!-- Section 4: Live Preview Card -->
          <v-card
            class="member-profile-card preview-main-card slide-up-animation-4 flex-grow-1"
            elevation="3"
          >
            <div class="card-section-title mb-6">
              <v-icon class="mr-2" color="primary">mdi-eye-outline</v-icon>
              <span>LO Name Preview</span>
            </div>

            <div class="preview-box-container pa-5 rounded-24 text-center">
              <div
                class="text-caption font-weight-black text-uppercase grey--text tracking-wide mb-1"
              >
                Current Name
              </div>
              <div class="preview-name-old-txt mb-4">
                {{ LomName || "N/A" }}
              </div>

              <div class="my-3 transition-arrow-box">
                <v-icon color="#0097D7" size="36" class="pulse-arrow"
                  >mdi-arrow-down-bold-circle-outline</v-icon
                >
              </div>

              <div
                class="text-caption font-weight-black text-uppercase tracking-wide mb-2 label-proposed"
              >
                Proposed Name
              </div>
              <div class="preview-badge-glowing-wrapper">
                <div class="preview-badge-new text-uppercase">
                  {{ ProposedLoName }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Row 3: LO Rename Fee & Order Summary -->
        <v-col cols="12" md="6">
          <!-- Section 5: Fee & Charges -->
          <v-card
            class="member-profile-card slide-up-animation-5"
            elevation="2"
          >
            <div class="card-section-title mb-4">
              <v-icon class="mr-2" color="#0097D7">mdi-cash-register</v-icon>
              <span>LO Rename Fee</span>
            </div>

            <div class="price-details-card pa-4 rounded-xl">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="price-lbl text-uppercase"
                  >Base Processing Fee</span
                >
                <span
                  class="font-weight-bold"
                  style="color: #1e3a8a; font-size: 18px"
                  >₹{{
                    SubTotal.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span
                >
              </div>
              <template v-if="inter_state">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="price-lbl text-uppercase">IGST (18%)</span>
                  <span
                    class="font-weight-bold"
                    style="color: #1e3a8a; font-size: 18px"
                    >₹{{
                      Igst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
              </template>
              <template v-else>
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="price-lbl text-uppercase">CGST (9%)</span>
                  <span
                    class="font-weight-bold"
                    style="color: #1e3a8a; font-size: 18px"
                    >₹{{
                      Cgst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="price-lbl text-uppercase">SGST (9%)</span>
                  <span
                    class="font-weight-bold"
                    style="color: #1e3a8a; font-size: 18px"
                    >₹{{
                      Sgst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
              </template>
              <v-divider
                class="my-2"
                style="border-color: rgba(86, 189, 163, 0.25)"
              ></v-divider>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <span
                    class="price-lbl text-uppercase d-block font-weight-bold"
                    style="font-size: 14px"
                    >Grand Total</span
                  >
                  <span class="price-desc grey--text text-caption"
                    >Inclusive of all taxes</span
                  >
                </div>
                <span class="price-val"
                  >₹{{
                    GrandTotal.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span
                >
              </div>
            </div>

            <div class="d-flex align-start mt-3 px-1">
              <v-icon small class="mr-2 mt-1" color="#3A67B1"
                >mdi-information-outline</v-icon
              >
              <span class="grey--text text--darken-2 text-caption">
                The name change processing fee is non-refundable and subject to
                approval from the National Board.
              </span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex flex-column gap-6">
          <!-- Section 6 & 7: Declaration & Order Summary -->
          <v-card
            class="member-profile-card slide-up-animation-6"
            elevation="3"
          >
            <div class="card-section-title mb-6">
              <v-icon class="mr-2" color="primary">mdi-receipt</v-icon>
              <span>Order Summary</span>
            </div>

            <div class="checkout-summary-list mb-4">
              <div class="summary-row d-flex justify-space-between py-3">
                <span class="summary-lbl">Current LO Name</span>
                <span class="summary-val font-weight-medium text-right">{{
                  LomName || "N/A"
                }}</span>
              </div>
              <div class="summary-row d-flex justify-space-between py-3">
                <span class="summary-lbl">Proposed LO Name</span>
                <span
                  class="summary-val font-weight-bold text-primary text-right"
                  >{{ ProposedLoName }}</span
                >
              </div>
              <div class="summary-row d-flex justify-space-between py-3">
                <span class="summary-lbl">Jurisdiction Selected</span>
                <span class="summary-val font-weight-medium text-right">{{
                  getSelectedJurisdiction
                }}</span>
              </div>
              <div class="summary-row d-flex justify-space-between py-2">
                <span class="summary-lbl">Base Processing Fee</span>
                <span class="summary-val font-weight-medium text-right"
                  >₹{{
                    SubTotal.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span
                >
              </div>
              <template v-if="inter_state">
                <div class="summary-row d-flex justify-space-between py-2">
                  <span class="summary-lbl">IGST (18%)</span>
                  <span class="summary-val font-weight-medium text-right"
                    >₹{{
                      Igst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
              </template>
              <template v-else>
                <div class="summary-row d-flex justify-space-between py-2">
                  <span class="summary-lbl">CGST (9%)</span>
                  <span class="summary-val font-weight-medium text-right"
                    >₹{{
                      Cgst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
                <div class="summary-row d-flex justify-space-between py-2">
                  <span class="summary-lbl">SGST (9%)</span>
                  <span class="summary-val font-weight-medium text-right"
                    >₹{{
                      Sgst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    }}</span
                  >
                </div>
              </template>
              <div
                class="summary-row d-flex justify-space-between py-3 grand-total-row"
              >
                <span class="summary-lbl font-weight-bold text-h5"
                  >Grand Total</span
                >
                <span
                  class="summary-val font-weight-black text-h5 primary--text"
                  >₹{{
                    GrandTotal.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}</span
                >
              </div>
            </div>

            <!-- Checkbox Declaration -->
            <v-checkbox
              v-model="declaration"
              color="primary"
              hide-details
              class="ma-0 pa-2 declaration-check-box rounded-xl"
            >
              <template v-slot:label>
                <div
                  class="checkbox-label text-caption text-justify grey--text text--darken-3 font-weight-medium pl-1"
                >
                  I hereby declare that the proposed name extension has been
                  approved by the local general body and conforms strictly to
                  JCI India guidelines.
                </div>
              </template>
            </v-checkbox>
          </v-card>

          <!-- Action Button -->
          <v-btn
            block
            depressed
            x-large
            :disabled="!valid || !declaration || submitLoading"
            :loading="submitLoading"
            class="action-proceed-btn py-7 elevation-4 slide-up-animation-7"
            @click="proceedToPayment"
          >
            <span>Proceed to Payment</span>
            <v-icon right size="22" class="ml-2"
              >mdi-credit-card-outline</v-icon
            >
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  name: "LoRenameCreate",
  mixins: [common],
  data() {
    return {
      loading: true,
      valid: false,
      declaration: false,
      submitLoading: false,

      // Current LO Data
      LomCode: "",
      LomName: "",
      StateName: "",
      DistrictName: "",
      CityName: "",
      ZoneName: "",
      LomStatusTxt: "Active",
      StateCode: "",
      DistrictCode: "",
      CityCode: "",

      // President Data
      PresidentName: "",
      PresidentId: "",
      PresidentMobile: "",
      PresidentEmail: "",
      PresidentPhoto: "",

      // Form Data
      NewStateCode: "",
      NewDistrictCode: "",
      NewCityCode: "",
      NewLoName: "",

      MobileNo: "",
      EmailId: "",
      Order: null,

      // Dropdown Options
      StateCodeOptions: [],
      StateCodeOptionsLoading: false,
      DistrictCodeOptions: [],
      DistrictCodeOptionsLoading: false,
      CityCodeOptions: [],
      CityCodeOptionsLoading: false,
    };
  },
  computed: {
    currentLoFields() {
      return [
        { label: "LO Name", value: this.LomName, icon: "mdi-office-building" },
        { label: "LO Code", value: this.LomCode, icon: "mdi-barcode" },
        { label: "Zone", value: this.ZoneName, icon: "mdi-map-marker-radius" },
        { label: "State", value: this.StateName, icon: "mdi-map" },
        { label: "District", value: this.DistrictName, icon: "mdi-city" },
        { label: "City", value: this.CityName, icon: "mdi-home-map-marker" },
      ];
    },
    badgeStyle() {
      const isRed = this.LomStatusTxt && this.LomStatusTxt !== "Active";
      return {
        color: isRed ? "#E53935" : "#56bda3",
        borderColor: isRed
          ? "rgba(229, 57, 85, 0.2)"
          : "rgba(86, 189, 163, 0.2)",
        background: isRed
          ? "rgba(229, 57, 85, 0.05)"
          : "rgba(86, 189, 163, 0.05)",
      };
    },
    dotStyle() {
      const isRed = this.LomStatusTxt && this.LomStatusTxt !== "Active";
      return {
        backgroundColor: isRed ? "#E53935" : "#56bda3",
        animation: isRed
          ? "pulseRed 1.8s infinite"
          : "pulseGreen 1.8s infinite",
      };
    },
    ProposedLoName() {
      const cityObj = this.CityCodeOptions.find(
        (o) => o.value === this.NewCityCode
      );
      const cityName = cityObj ? cityObj.text : "{city name}";

      const ext = this.NewLoName ? this.NewLoName.trim() : "{extension}";
      return `jci ${cityName} ${ext}`.toUpperCase().replace(/\s+/g, " ");
    },
    getSelectedJurisdiction() {
      const stateObj = this.StateCodeOptions.find(
        (o) => o.value === this.NewStateCode
      );
      const distObj = this.DistrictCodeOptions.find(
        (o) => o.value === this.NewDistrictCode
      );
      const cityObj = this.CityCodeOptions.find(
        (o) => o.value === this.NewCityCode
      );

      const s = stateObj ? stateObj.text : "";
      const d = distObj ? distObj.text : "";
      const c = cityObj ? cityObj.text : "";

      if (s || d || c) {
        return [c, d, s].filter(Boolean).join(", ");
      }
      return "N/A";
    },
    inter_state() {
      const sellerStateId = 21;
      const buyerStateId = parseInt(this.NewStateCode);
      return buyerStateId > 0 && buyerStateId !== sellerStateId;
    },
    SubTotal() {
      return 5000;
    },
    Cgst() {
      return !this.inter_state ? this.SubTotal * 0.09 : 0;
    },
    Sgst() {
      return !this.inter_state ? this.SubTotal * 0.09 : 0;
    },
    Igst() {
      return this.inter_state ? this.SubTotal * 0.18 : 0;
    },
    TotalGst() {
      return this.Cgst + this.Sgst + this.Igst;
    },
    GrandTotal() {
      return this.SubTotal + this.TotalGst;
    },
  },
  watch: {
    NewStateCode: function (newVal) {
      if (newVal) {
        this.getDistrictCodeOptions(
          "NewDistrictCode",
          "DistrictCodeOptions",
          newVal
        );
      } else {
        this.DistrictCodeOptions = [];
        this.NewDistrictCode = "";
      }
    },
    NewDistrictCode: function (newVal) {
      if (newVal) {
        this.getCityCodeOptions("NewCityCode", "CityCodeOptions", newVal);
      } else {
        this.CityCodeOptions = [];
        this.NewCityCode = "";
      }
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    getParticleStyle(n) {
      const styles = [
        {
          top: "10%",
          left: "8%",
          animationDelay: "0s",
          background: "rgba(255,255,255,0.15)",
        },
        {
          top: "65%",
          left: "20%",
          animationDelay: "1.2s",
          background: "rgba(255,255,255,0.2)",
        },
        {
          top: "30%",
          left: "85%",
          animationDelay: "0.6s",
          background: "rgba(255,255,255,0.12)",
        },
        {
          top: "80%",
          left: "70%",
          animationDelay: "1.8s",
          background: "rgba(255,255,255,0.25)",
        },
        {
          top: "15%",
          left: "50%",
          animationDelay: "2.4s",
          background: "rgba(255,255,255,0.18)",
        },
        {
          top: "50%",
          left: "60%",
          animationDelay: "0.3s",
          background: "rgba(255,255,255,0.1)",
        },
      ];
      return styles[n - 1] || {};
    },
    loadLomData() {
      this.loading = true;
      const LomId = this.$session.get("LomId");
      this.LomCode = LomId;
      const token = this.$session.get("token");

      if (!LomId) {
        this.loading = false;
        this.toast("error", "LOM ID not found in session");
        return;
      }

      // 1. Fetch LO details & President info from api/lom/show
      this.$http
        .post(
          companyConfig.apiURL + "api/lom/show",
          {
            UserInterface: 1,
            Lom: LomId,
          },
          {
            headers: { Authorization: "Bearer " + token },
          }
        )
        .then((response) => {
          if (response.data && response.data.flag == 1) {
            const records = response.data.records;
            this.LomName = records.LomName || "";
            this.LomCode = records.LomId || LomId;
            this.StateName = records.StateName || "N/A";
            this.DistrictName = records.DistrictName || "N/A";
            this.CityName = records.CityName || "N/A";
            this.ZoneName = records.ZoneName || "N/A";
            this.LomStatusTxt = records.LomStatusTxt || "Active";
            this.StateCode = records.StateId || "";
            this.DistrictCode = records.DistrictId || "";
            this.CityCode = records.CityId || "";

            // Populate President details
            if (records.President) {
              this.PresidentName =
                records.President.LomPresidentFullName || "N/A";
              this.PresidentId =
                records.President.LomPresidentMembershipId || "N/A";
              this.PresidentMobile =
                records.President.LomPresidentMobileNo || "N/A";
              this.PresidentEmail =
                records.President.LomPresidentEmailid || "N/A";
              this.PresidentPhoto = records.President.MemberImagePath || "";
              this.MobileNo = records.President.LomPresidentMobileNo || "";
              this.EmailId = records.President.LomPresidentEmailid || "";
            }

            // Pre-populate New Form Dropdowns
            this.getStateCodeOptions("NewStateCode", "StateCodeOptions");
          }
          this.loading = false;
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
          this.toast("error", "Failed to fetch current LOM details");
        });
    },
    getStateCodeOptions(source, destination) {
      this.StateCodeOptionsLoading = true;
      const selectbox1_source = source;
      const selectbox1_destination = destination;
      const selectbox1_url = "api/states/options";
      const selectbox1_conditions_array = { UserInterface: 1 };

      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );

      const checkStateLoad = setInterval(() => {
        if (this.StateCodeOptions && this.StateCodeOptions.length > 1) {
          clearInterval(checkStateLoad);
          this.StateCodeOptionsLoading = false;
          if (this.StateCode) {
            this.NewStateCode = this.StateCode;
            this.defaultDistrictAndCity();
          }
        }
      }, 100);
    },
    defaultDistrictAndCity() {
      const checkDistrictLoad = setInterval(() => {
        if (this.DistrictCodeOptions && this.DistrictCodeOptions.length > 1) {
          clearInterval(checkDistrictLoad);
          if (this.DistrictCode) {
            this.NewDistrictCode = this.DistrictCode;
            this.defaultCity();
          }
        }
      }, 100);
    },
    defaultCity() {
      const checkCityLoad = setInterval(() => {
        if (this.CityCodeOptions && this.CityCodeOptions.length > 1) {
          clearInterval(checkCityLoad);
          if (this.CityCode) {
            this.NewCityCode = this.CityCode;
          }
        }
      }, 100);
    },
    getDistrictCodeOptions(source, destination, StateCode) {
      this.DistrictCodeOptionsLoading = true;
      const selectbox1_source = source;
      const selectbox1_destination = destination;
      const selectbox1_url = "api/district/options";
      const selectbox1_conditions_array = {
        UserInterface: 1,
        StatesCode: StateCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );

      const checkLoad = setInterval(() => {
        if (this.DistrictCodeOptions && this.DistrictCodeOptions.length > 0) {
          clearInterval(checkLoad);
          this.DistrictCodeOptionsLoading = false;
        }
      }, 100);
    },
    getCityCodeOptions(source, destination, DistrictCode) {
      this.CityCodeOptionsLoading = true;
      const selectbox1_source = source;
      const selectbox1_destination = destination;
      const selectbox1_url = "api/city/options";
      const selectbox1_conditions_array = {
        UserInterface: 2,
        DistrictCode: DistrictCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );

      const checkLoad = setInterval(() => {
        if (this.CityCodeOptions && this.CityCodeOptions.length > 0) {
          clearInterval(checkLoad);
          this.CityCodeOptionsLoading = false;
        }
      }, 100);
    },
    proceedToPayment() {
      if (this.$refs.form.validate() && this.declaration) {
        this.submitLoading = true;

        let htmlTxt = "";
        htmlTxt +=
          "<b style='color: #1E3A8A;'>Please verify the renaming details before payment:</b><br/><br/>";
        htmlTxt +=
          "<div class='text-left pa-3' style='background:rgba(255, 255, 255, 0.9); border: 1px solid rgba(58, 103, 177, 0.1); border-radius: 12px; color: #1E3A8A;'>";
        htmlTxt +=
          "Current LO Name: <b style='color: #3A67B1;'>" +
          this.LomName +
          "</b><br/>";
        htmlTxt +=
          "Proposed LO Name: <b style='color: #0097D7;'>" +
          this.ProposedLoName +
          "</b><br/>";
        htmlTxt +=
          "Jurisdiction: <b style='color: #3A67B1;'>" +
          this.getSelectedJurisdiction +
          "</b><br/>";
        htmlTxt +=
          "Base Processing Fee: <b style='color: #3A67B1;'>₹" +
          this.SubTotal.toFixed(2) +
          "</b><br/>";
        if (this.inter_state) {
          htmlTxt +=
            "IGST (18%): <b style='color: #3A67B1;'>₹" +
            this.Igst.toFixed(2) +
            "</b><br/>";
        } else {
          htmlTxt +=
            "CGST (9%): <b style='color: #3A67B1;'>₹" +
            this.Cgst.toFixed(2) +
            "</b><br/>";
          htmlTxt +=
            "SGST (9%): <b style='color: #3A67B1;'>₹" +
            this.Sgst.toFixed(2) +
            "</b><br/>";
        }
        htmlTxt +=
          "Amount Payable: <b style='color: #56BDA3;'>₹" +
          this.GrandTotal.toFixed(2) +
          "</b>";
        htmlTxt += "</div>";
        htmlTxt +=
          "<br/><span style='color: #3A67B1;'>Do you want to proceed to checkout?</span>";

        Swal.fire({
          title: "Confirm Rename Checkout",
          html: htmlTxt,
          icon: "info",
          background: "#ffffff",
          confirmButtonColor: "#1E3A8A",
          cancelButtonColor: "#56BDA3",
          confirmButtonText: "Yes, Proceed",
          cancelButtonText: "Cancel",
          showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            this.createOrder();
          } else {
            this.submitLoading = false;
          }
        });
      }
    },
    createOrder() {
      this.submitLoading = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-rename/order-create";

      var upload = {
        UserInterface: 1,
        OldLoName: this.LomName,
        NewLoName: this.ProposedLoName,
        MobileNo: this.MobileNo,
        EmailId: this.EmailId,
        LoState: this.NewStateCode,
        LoDistrict: this.NewDistrictCode,
        LoCity: this.NewCityCode,
        TotalAmount: this.GrandTotal,
        GstNumber: this.GstNo || null,
      };

      const thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.submitLoading = false;
          var flag = response.data.flag;
          var output = response.data.output;
          var records = response.data.records;

          if (flag == 1 && records && records.Order) {
            thisIns.Order = records.Order;
            thisIns.makePayment();
          } else {
            thisIns.sweetAlert(
              "error",
              output || "Failed to create payment order.",
              false
            );
          }
        })
        .catch(function (error) {
          thisIns.submitLoading = false;
          console.log("error=" + error);
          thisIns.sweetAlert(
            "error",
            "An error occurred while creating the payment order.",
            false
          );
        });
    },
    makePayment() {
      var order = this.Order;
      const thisIns = this;
      var rzp1 = new window.Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description || "LO Rename Request",
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.submitLoading = true;
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  mounted() {
    this.loadLomData();
  },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-purchase-experience {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background: linear-gradient(180deg, #ffffff 0%, #f5f9ff 50%, #eef8ff 100%);
  min-height: 100vh;
  position: relative;
  overflow: visible;
  padding-bottom: 60px;

  // --- Dynamic Animated Backgrounds ---
  .animated-mesh-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 600px;
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.04) 0%,
      rgba(0, 151, 215, 0.04) 50%,
      rgba(86, 189, 163, 0.04) 100%
    );
    z-index: 0;
  }

  .background-decorations {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;

    .blur-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.35;
      mix-blend-mode: multiply;
      animation: morphBlob 12s infinite alternate ease-in-out;
    }

    .blob-1 {
      top: -10%;
      right: 5%;
      width: 400px;
      height: 400px;
      background-color: rgba(58, 103, 177, 0.08); // Navy Blue
    }

    .blob-2 {
      top: 30%;
      left: -10%;
      width: 350px;
      height: 350px;
      background-color: rgba(86, 189, 163, 0.06); // Sea Green
      animation-delay: -3s;
    }

    .blob-3 {
      bottom: 10%;
      right: 10%;
      width: 450px;
      height: 450px;
      background-color: rgba(0, 151, 215, 0.08); // Aqua Blue
      animation-delay: -6s;
    }
  }

  @keyframes morphBlob {
    0% {
      transform: translate(0, 0) scale(1) rotate(0deg);
      border-radius: 50%;
    }
    33% {
      transform: translate(30px, -40px) scale(1.1) rotate(60deg);
      border-radius: 40% 60% 50% 50%;
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9) rotate(-40deg);
      border-radius: 60% 40% 30% 70%;
    }
    100% {
      transform: translate(0, 0) scale(1) rotate(0deg);
      border-radius: 50%;
    }
  }

  .max-width-container {
    max-width: 100% !important;
    position: relative;
    z-index: 1;
  }

  // --- HERO BANNER ---
  .hero-banner {
    position: relative;
    background: linear-gradient(
      135deg,
      #1e3a8a 0%,
      #3a67b1 35%,
      #0097d7 70%,
      #56bda3 100%
    ) !important;
    background-size: 400% 400% !important;
    animation: gradientShift 15s ease infinite !important;
    border-radius: 28px;
    padding: 30px 40px;
    min-height: 180px;
    overflow: hidden;
    color: #ffffff;
    box-shadow: 0 25px 50px rgba(58, 103, 177, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    margin-bottom: 24px;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle at 80% 20%,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 60%
      );
      pointer-events: none;
    }

    .back-btn-blur {
      background: rgba(255, 255, 255, 0.15) !important;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.25);
      transition: all 0.3s ease;
      width: 44px;
      height: 44px;

      &:hover {
        background: rgba(255, 255, 255, 0.25) !important;
        transform: translateX(-3px);
      }
    }

    .header-icon-box {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.25) 0%,
        rgba(255, 255, 255, 0.1) 100%
      );
      backdrop-filter: blur(12px);
      border-radius: 16px;
      padding: 12px;
      border: 2px solid rgba(255, 255, 255, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.2);
    }

    .badge-hero {
      display: inline-flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 20px;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      width: fit-content;
      color: #ffffff;
    }

    .hero-title {
      font-size: 32px;
      font-weight: 900;
      line-height: 1.2;
      letter-spacing: -0.5px;
      color: white;
    }

    .text-gradient-sky {
      background: linear-gradient(90deg, #56bda3 0%, #ffffff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-weight: 850;
    }

    .hero-subtitle {
      font-size: 14.5px;
      line-height: 1.5;
      opacity: 0.9;
      margin-top: 8px;
      max-width: 750px;
      color: rgba(255, 255, 255, 0.9);
    }

    .particle {
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      pointer-events: none;
      animation: floatParticle 8s infinite linear;
    }
  }

  @keyframes floatParticle {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.3;
    }
    50% {
      transform: translateY(-20px) scale(1.2);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.3;
    }
  }

  // --- GLASS PROFILE CARD (Professional Layout) ---
  .member-profile-card {
    background: rgba(255, 255, 255, 0.92) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(58, 103, 177, 0.08) !important;
    border-radius: 24px !important;
    padding: 24px 28px !important;
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(58, 103, 177, 0.08),
      0 2px 10px rgba(0, 151, 215, 0.05) !important;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);

    &:hover {
      transform: translateY(-3px) scale(1.01);
      box-shadow: 0 15px 35px rgba(58, 103, 177, 0.12),
        0 4px 15px rgba(0, 151, 215, 0.08) !important;
      border-color: rgba(86, 189, 163, 0.25) !important;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(
        90deg,
        #1e3a8a 0%,
        #3a67b1 35%,
        #0097d7 70%,
        #56bda3 100%
      );
    }

    .card-section-title {
      font-size: 21px;
      font-weight: 800;
      color: #1e3a8a;
      display: flex;
      align-items: center;
      letter-spacing: -0.3px;

      .v-icon {
        color: #0097d7 !important;
      }
    }

    .profile-header-badge {
      position: absolute;
      top: 18px;
      right: 18px;
      background: rgba(86, 189, 163, 0.1);
      color: #56bda3;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 750;
      letter-spacing: 0.2px;
      text-transform: uppercase;
      border: 1px solid rgba(86, 189, 163, 0.2);
      display: flex;
      align-items: center;
    }

    .badge-status-dot {
      width: 6px;
      height: 6px;
      background-color: #56bda3;
      border-radius: 50%;
      display: inline-block;
      margin-right: 6px;
      animation: pulseGreen 1.8s infinite;
    }

    // Details Grid Item
    .member-field-item {
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(58, 103, 177, 0.06);
      padding: 12px 16px;
      border-radius: 16px;
      transition: all 0.3s ease;

      &:hover {
        background: #ffffff;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(58, 103, 177, 0.04);
        border-color: rgba(0, 151, 215, 0.2);
      }

      .icon-wrap-bg {
        background: rgba(0, 151, 215, 0.08);
        padding: 8px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        .v-icon {
          color: #0097d7 !important;
        }
      }

      .field-lbl {
        font-size: 13px;
        font-weight: 700;
        color: #3a67b1;
        text-transform: uppercase;
        display: block;
        margin-bottom: 2px;
        letter-spacing: 0.5px;
      }

      .field-val {
        font-size: 17px;
        font-weight: 700;
        color: #1e3a8a;
        display: block;
      }
    }

    // Avatar Box
    .avatar-glow {
      position: relative;
      width: 96px;
      height: 96px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1565c0 0%, #0097d7 100%);
      padding: 3px;
      box-shadow: 0 8px 20px rgba(21, 101, 192, 0.15);

      .profile-avatar-initials {
        background: #ffffff;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        .profile-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }
      }
    }

    .member-title-name {
      font-size: 25px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
    }

    .member-subtitle-id {
      font-size: 15px;
      font-weight: 600;
      color: #64748b;

      .id-pill {
        background: #f1f5f9;
        padding: 4px 10px;
        border-radius: 6px;
        font-weight: 700;
      }
    }

    // Form inputs labels
    .field-label-custom {
      font-size: 15px !important;
      font-weight: 700 !important;
      color: #475569 !important;
      margin-bottom: 8px !important;
      display: block;
    }
  }

  // --- PREVIEW CARD ---
  .preview-main-card {
    &::before {
      background: linear-gradient(90deg, #0097d7 0%, #56bda3 100%);
    }
  }

  .preview-box-container {
    background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
    border: 2px dashed rgba(21, 101, 192, 0.2);
    position: relative;
    overflow: hidden;

    .preview-name-old-txt {
      font-size: 21px;
      font-weight: 700;
      color: #94a3b8;
      text-decoration: line-through;
    }

    .pulse-arrow {
      animation: arrowFloat 2s infinite ease-in-out;
    }

    .label-proposed {
      color: #56bda3 !important;
      font-weight: 850;
      font-size: 14px;
      letter-spacing: 0.8px;
    }

    .preview-badge-glowing-wrapper {
      position: relative;
      display: inline-block;
      max-width: 100%;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, #0097d7 0%, #56bda3 100%);
        border-radius: 16px;
        filter: blur(10px);
        opacity: 0.35;
        z-index: 1;
      }
    }

    .preview-badge-new {
      position: relative;
      z-index: 2;
      background: linear-gradient(135deg, #0097d7 0%, #56bda3 100%);
      color: white;
      padding: 12px 28px;
      font-size: 22px;
      font-weight: 900;
      letter-spacing: 0.5px;
      border-radius: 16px;
      box-shadow: 0 10px 25px rgba(0, 151, 215, 0.25);
      text-shadow: 0 1.5px 3px rgba(0, 0, 0, 0.15);
      word-wrap: break-word;
      word-break: break-all;
    }
  }

  @keyframes arrowFloat {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(6px);
    }
  }

  // --- PRICE CONTAINER ---
  .price-details-card {
    background: rgba(86, 189, 163, 0.06);
    border: 1px solid rgba(86, 189, 163, 0.15);

    .price-lbl {
      font-size: 13px;
      font-weight: 800;
      color: #56bda3;
      letter-spacing: 0.6px;
    }

    .price-val {
      font-size: 34px;
      font-weight: 900;
      color: #1e3a8a;
    }
  }

  // --- CHECKOUT SUMMARY ---
  .checkout-summary-list {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 18px;
    border: 1px solid rgba(58, 103, 177, 0.08);
    padding: 8px 16px;

    .summary-row {
      border-bottom: 1px solid rgba(58, 103, 177, 0.08);
      font-size: 16.5px;
      align-items: center;

      &:last-child {
        border-bottom: none;
      }

      .summary-lbl {
        color: #3a67b1;
        font-weight: 600;
      }

      .summary-val {
        color: #1e3a8a;
      }
    }

    .grand-total-row {
      .summary-lbl {
        color: #1e3a8a !important;
      }
    }
  }

  // Declaration Wrapper
  .declaration-check-box {
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(58, 103, 177, 0.08);

    .v-label {
      color: #3a67b1 !important;
    }
  }

  // --- PAY PROCEED BTN ---
  .action-proceed-btn {
    background: linear-gradient(135deg, #1e3a8a 0%, #3a67b1 100%) !important;
    color: white !important;
    font-weight: 850 !important;
    font-size: 16px !important;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    border-radius: 16px !important;
    transition: all 0.3s ease;

    &:hover:not(.v-btn--disabled) {
      transform: translateY(-3px);
      box-shadow: 0 12px 30px rgba(30, 58, 138, 0.25) !important;
    }
  }

  // Font family helper class
  .font-outfit {
    font-family: "Outfit", sans-serif !important;
  }

  // Vuetify Input / Select Custom Styling Overrides for Premium Light Theme
  .theme--light.v-text-field--outlined:not(.v-input--is-focused):not(
      .v-input--has-state
    )
    > .v-input__control
    > .v-input__slot
    fieldset {
    border-color: rgba(58, 103, 177, 0.15) !important;
  }

  .theme--light.v-text-field--outlined:not(.v-input--is-focused):not(
      .v-input--has-state
    ):hover
    > .v-input__control
    > .v-input__slot
    fieldset {
    border-color: rgba(0, 151, 215, 0.3) !important;
  }

  .theme--light.v-text-field--outlined.v-input--is-focused
    > .v-input__control
    > .v-input__slot
    fieldset {
    border-color: #0097d7 !important;
    border-width: 2px;
  }

  .theme--light.v-text-field--outlined.v-input--is-focused {
    .v-text-field__slot input {
      color: #1e3a8a !important;
    }
    .v-label {
      color: #0097d7 !important;
    }
  }

  // Animations & Delays
  .slide-up-animation {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .slide-up-animation-1 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s backwards;
  }
  .slide-up-animation-2 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s backwards;
  }
  .slide-up-animation-3 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s backwards;
  }
  .slide-up-animation-4 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s backwards;
  }
  .slide-up-animation-5 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s backwards;
  }
  .slide-up-animation-6 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s backwards;
  }
  .slide-up-animation-7 {
    animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s backwards;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes pulseGreen {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(86, 189, 163, 0.3);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 6px rgba(86, 189, 163, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(86, 189, 163, 0);
    }
  }

  @keyframes pulseRed {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(229, 57, 85, 0.3);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 6px rgba(229, 57, 85, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(229, 57, 85, 0);
    }
  }
}

// Autocomplete list overrides in light mode
.v-menu__content.theme--light,
.v-autocomplete__content.theme--light {
  background-color: #ffffff !important;
  border: 1px solid rgba(58, 103, 177, 0.12) !important;
  box-shadow: 0 10px 30px rgba(58, 103, 177, 0.08) !important;
  border-radius: 12px !important;

  .v-list {
    background: #ffffff !important;
    color: #1e3a8a !important;
    padding: 6px 0 !important;

    .v-list-item {
      color: #1e3a8a !important;
      font-family: "Outfit", sans-serif !important;
      font-size: 14px !important;
      min-height: 40px !important;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(0, 151, 215, 0.08) !important;
      }

      &.v-list-item--active {
        color: #56bda3 !important;
        background-color: rgba(86, 189, 163, 0.08) !important;
        font-weight: 700;
      }
    }
  }
}

.gap-4 {
  gap: 16px;
}
.gap-6 {
  gap: 24px;
}
.tracking-wide {
  letter-spacing: 0.8px;
}
</style>
